import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Grid } from 'react-virtualized'

import Loading from '../../../../components/Loading'
import { useFullCatalog } from '../../hooks/use-catalog-full'
import { buildCatalogSorter } from '../../utils/catalog-filters'
import { CardTop } from '../card-top'
import { SectionTitle } from '../top-games/top-games.styled'

import {
  EmptyState,
  GridCell,
  GridCellInner,
  GridContainer,
  LoadingWrapper,
  Section,
} from './all-catalog.styled'

const GAP = 14
const PADDING = 0
const MIN_COLUMN_WIDTH = 160

const getGridDimensions = (containerWidth) => {
  const availableWidth = containerWidth - PADDING * 2
  const columnCount = Math.max(
    1,
    Math.floor((availableWidth + GAP) / (MIN_COLUMN_WIDTH + GAP))
  )
  const columnWidth = Math.floor(
    (availableWidth - GAP * (columnCount - 1)) / columnCount
  )

  return { columnCount, columnWidth, rowHeight: columnWidth + GAP }
}

const getCategoryLabel = (selectedType) =>
  selectedType === 'all' ? 'Todos' : selectedType

const getCatalogTitle = (selectedType, searchText) => {
  const query = searchText.trim()
  const category = getCategoryLabel(selectedType)

  if (query.length > 0) {
    return `Resultados de "${query}" en ${category}`
  }

  if (selectedType !== 'all') {
    return `Juegos de ${selectedType}`
  }

  return 'Todos los juegos'
}

export const AllCatalog = memo(function AllCatalog({
  selectedType = 'all',
  searchText = '',
  sortBy = 'rank',
  sortDirection = 1,
  scrollElement = null,
  onLaunchGame,
}) {
  const { games, isLoading, error } = useFullCatalog()
  const sectionRef = useRef(null)
  const gridAnchorRef = useRef(null)
  const sortKey = `${sortBy}-${sortDirection}`
  const prevSortKeyRef = useRef(sortKey)

  const [scrollMetrics, setScrollMetrics] = useState({
    scrollTop: 0,
    viewportHeight: 0,
    gridOffset: 0,
  })
  const [containerWidth, setContainerWidth] = useState(0)

  const layoutKey = useMemo(
    () => `${selectedType}|${searchText.trim()}`,
    [selectedType, searchText]
  )

  const hasActiveCatalogFilter =
    selectedType !== 'all' || searchText.trim().length > 0

  const catalogTitle = useMemo(
    () => getCatalogTitle(selectedType, searchText),
    [selectedType, searchText]
  )

  const filteredGames = useMemo(() => {
    const safeGames = Array.isArray(games) ? games.filter(Boolean) : []
    let result = safeGames

    if (selectedType !== 'all') {
      result = result.filter((game) => game.game_type === selectedType)
    }

    if (searchText !== '' && searchText.length > 2) {
      const query = searchText.toLowerCase()
      result = result.filter((game) => game.name.toLowerCase().includes(query))
    }

    return [...result].sort(buildCatalogSorter(sortBy, sortDirection))
  }, [games, selectedType, searchText, sortBy, sortDirection])

  const gridLayout = useMemo(() => {
    if (containerWidth <= 0) {
      return null
    }

    const { columnCount, columnWidth, rowHeight } =
      getGridDimensions(containerWidth)

    return {
      width: containerWidth,
      columnCount,
      columnWidth,
      rowHeight,
      rowCount: Math.ceil(filteredGames.length / columnCount),
    }
  }, [containerWidth, filteredGames.length])

  const cellRenderer = useCallback(
    ({ columnIndex, key, rowIndex, style }) => {
      if (!gridLayout) {
        return null
      }

      const { columnCount, columnWidth } = gridLayout
      const index = rowIndex * columnCount + columnIndex

      if (index >= filteredGames.length) {
        return null
      }

      return (
        <GridCell
          key={key}
          style={{
            ...style,
            left: style.left + PADDING,
            top: style.top + PADDING,
            width: columnWidth,
            height: columnWidth,
          }}
        >
          <GridCellInner>
            <CardTop
              game={filteredGames[index]}
              compactAspect
              instant
              hoverScale={1.04}
              onLaunchGame={
                onLaunchGame
                  ? (isDemo) => onLaunchGame(filteredGames[index], isDemo)
                  : undefined
              }
            />
          </GridCellInner>
        </GridCell>
      )
    },
    [filteredGames, gridLayout, onLaunchGame]
  )

  const handleGridScroll = useCallback(
    ({ scrollTop }) => {
      if (!scrollElement) return

      const gridOffset = gridAnchorRef.current?.offsetTop ?? 0
      const targetScrollTop = scrollTop + gridOffset

      if (Math.abs(scrollElement.scrollTop - targetScrollTop) > 1) {
        scrollElement.scrollTop = targetScrollTop
      }
    },
    [scrollElement]
  )

  const setGridAnchorRef = useCallback((node) => {
    gridAnchorRef.current = node

    if (node) {
      const nextWidth = node.offsetWidth
      setContainerWidth((prev) => (prev === nextWidth ? prev : nextWidth))
    }
  }, [])

  const gridKey = `${layoutKey}-${filteredGames.length}-${sortBy}-${sortDirection}`

  const syncScrollMetrics = useCallback(() => {
    if (!scrollElement) return

    const nextMetrics = {
      scrollTop: scrollElement.scrollTop,
      viewportHeight: scrollElement.clientHeight,
      gridOffset: gridAnchorRef.current?.offsetTop ?? 0,
    }

    setScrollMetrics((prev) => {
      if (
        prev.scrollTop === nextMetrics.scrollTop &&
        prev.viewportHeight === nextMetrics.viewportHeight &&
        prev.gridOffset === nextMetrics.gridOffset
      ) {
        return prev
      }

      return nextMetrics
    })
  }, [scrollElement])

  useEffect(() => {
    if (!scrollElement) return

    const syncLayout = () => {
      if (hasActiveCatalogFilter && sectionRef.current) {
        scrollElement.scrollTop = sectionRef.current.offsetTop
      } else {
        scrollElement.scrollTop = 0
      }

      syncScrollMetrics()
    }

    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(syncLayout)
    })

    return () => cancelAnimationFrame(frame)
  }, [scrollElement, layoutKey, hasActiveCatalogFilter, syncScrollMetrics])

  useEffect(() => {
    if (!scrollElement) return

    let frameId = 0

    const scheduleUpdate = () => {
      cancelAnimationFrame(frameId)
      frameId = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          syncScrollMetrics()

          const nextWidth = gridAnchorRef.current?.offsetWidth ?? 0
          setContainerWidth((prev) => (prev === nextWidth ? prev : nextWidth))
        })
      })
    }

    scheduleUpdate()

    const resizeObserver = new ResizeObserver(() => {
      scheduleUpdate()
    })
    resizeObserver.observe(scrollElement)

    const gridAnchor = gridAnchorRef.current
    if (gridAnchor) {
      resizeObserver.observe(gridAnchor)
    }

    scrollElement.addEventListener('scroll', scheduleUpdate, { passive: true })

    return () => {
      cancelAnimationFrame(frameId)
      resizeObserver.disconnect()
      scrollElement.removeEventListener('scroll', scheduleUpdate)
    }
  }, [scrollElement, syncScrollMetrics, isLoading, filteredGames.length])

  useEffect(() => {
    if (!scrollElement || prevSortKeyRef.current === sortKey) return

    prevSortKeyRef.current = sortKey

    const scrollToAllCatalog = () => {
      if (sectionRef.current) {
        scrollElement.scrollTop = sectionRef.current.offsetTop
      }
      syncScrollMetrics()
    }

    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(scrollToAllCatalog)
    })

    return () => cancelAnimationFrame(frame)
  }, [scrollElement, sortKey, syncScrollMetrics])

  if (isLoading) {
    return (
      <Section>
        <SectionTitle>{catalogTitle}</SectionTitle>
        <LoadingWrapper>
          <Loading />
        </LoadingWrapper>
      </Section>
    )
  }

  if (error) {
    return null
  }

  const gridScrollTop = Math.max(0, scrollMetrics.scrollTop - scrollMetrics.gridOffset)

  return (
    <Section ref={sectionRef}>
      <SectionTitle>{catalogTitle}</SectionTitle>

      {!filteredGames.length ? (
        <EmptyState>
          {searchText.length > 2
            ? `No se han encontrado juegos con el texto "${searchText}" En categoria ${getCategoryLabel(selectedType)}.`
            : 'No hay juegos disponibles.'}
        </EmptyState>
      ) : scrollElement ? (
        <GridContainer>
          <div ref={setGridAnchorRef} style={{ width: '100%', maxWidth: '100%', overflow: 'visible' }}>
            {gridLayout ? (
              <Grid
                key={gridKey}
                autoHeight
                width={gridLayout.width}
                height={scrollMetrics.viewportHeight}
                columnCount={gridLayout.columnCount}
                rowCount={gridLayout.rowCount}
                columnWidth={gridLayout.columnWidth + GAP}
                rowHeight={gridLayout.rowHeight}
                cellRenderer={cellRenderer}
                scrollTop={gridScrollTop}
                onScroll={handleGridScroll}
                containerStyle={{
                  paddingTop: 10,
                  paddingBottom: 10,
                  paddingLeft: 10,
                  paddingRight: 10,
                  overflow: 'visible',
                }}
                style={{
                  overflow: 'visible',
                  overflowX: 'visible',
                  overflowY: 'visible',
                }}
                overscanRowCount={3}
              />
            ) : null}
          </div>
        </GridContainer>
      ) : null}
    </Section>
  )
})
